import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';
import {
  FunnelIcon,
  ChartBarIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ArrowDownIcon,
  UsersIcon,
  ShoppingCartIcon,
  CreditCardIcon,
  EyeIcon,
  MagnifyingGlassIcon,
  CalendarIcon,
  AdjustmentsHorizontalIcon
} from '@heroicons/react/24/outline';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  AreaChart,
  Area,
  FunnelChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell
} from 'recharts';

const ConversionFunnels = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Data state
  const [funnelData, setFunnelData] = useState({});
  const [cartAbandonmentData, setCartAbandonmentData] = useState({});
  const [exitPagesData, setExitPagesData] = useState([]);
  const [engagementData, setEngagementData] = useState({});
  const [insights, setInsights] = useState([]);
  
  // Filter state
  const [dateRange, setDateRange] = useState('7d');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [selectedFunnel, setSelectedFunnel] = useState('default');

  // Predefined funnels
  const funnelTypes = {
    default: {
      name: 'משפך קנייה בסיסי',
      steps: ['page_view', 'product_view', 'add_to_cart', 'begin_checkout', 'purchase'],
      stepNames: ['צפייה בדף', 'צפייה במוצר', 'הוספה לעגלה', 'תחילת קנייה', 'רכישה']
    },
    detailed: {
      name: 'משפך קנייה מפורט',
      steps: ['page_view', 'category_view', 'product_list_view', 'product_view', 'add_to_cart', 'view_cart', 'begin_checkout', 'add_payment_info', 'purchase'],
      stepNames: ['צפייה בדף', 'צפייה בקטגוריה', 'רשימת מוצרים', 'צפייה במוצר', 'הוספה לעגלה', 'צפייה בעגלה', 'תחילת קנייה', 'פרטי תשלום', 'רכישה']
    },
    search: {
      name: 'משפך חיפוש',
      steps: ['search', 'product_list_view', 'product_view', 'add_to_cart', 'purchase'],
      stepNames: ['חיפוש', 'רשימת תוצאות', 'צפייה במוצר', 'הוספה לעגלה', 'רכישה']
    }
  };

  // Calculate date range
  const getDateRange = useCallback(() => {
    const now = new Date();
    let startDate, endDate = now;

    switch (dateRange) {
      case '1d':
        startDate = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        break;
      case '7d':
        startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case '30d':
        startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        break;
      case '90d':
        startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
        break;
      case 'custom':
        startDate = new Date(customStartDate);
        endDate = new Date(customEndDate);
        break;
      default:
        startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    }

    return { startDate, endDate };
  }, [dateRange, customStartDate, customEndDate]);

  // Fetch analytics data
  const fetchAnalyticsData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const { startDate, endDate } = getDateRange();
      const selectedSteps = funnelTypes[selectedFunnel].steps.join(',');

      const [funnelResponse, cartResponse, exitResponse, engagementResponse] = await Promise.all([
        axios.get('/api/user-journey/funnel-analysis', {
          params: { startDate: startDate.toISOString(), endDate: endDate.toISOString(), steps: selectedSteps },
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }),
        axios.get('/api/user-journey/cart-abandonment', {
          params: { startDate: startDate.toISOString(), endDate: endDate.toISOString() },
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }),
        axios.get('/api/user-journey/exit-pages', {
          params: { startDate: startDate.toISOString(), endDate: endDate.toISOString(), limit: 10 },
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }),
        axios.get('/api/user-journey/engagement-metrics', {
          params: { startDate: startDate.toISOString(), endDate: endDate.toISOString() },
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        })
      ]);

      setFunnelData(funnelResponse.data.data);
      setCartAbandonmentData(cartResponse.data.data);
      setExitPagesData(exitResponse.data.data);
      setEngagementData(engagementResponse.data.data);

      // Generate insights
      generateInsights(funnelResponse.data.data, cartResponse.data.data, engagementResponse.data.data);

    } catch (err) {
      setError(err.response?.data?.message || 'שגיאה בטעינת נתוני האנליטיקה');
      toast.error('שגיאה בטעינת נתוני האנליטיקה');
    } finally {
      setLoading(false);
    }
  }, [getDateRange, selectedFunnel]);

  // Generate insights
  const generateInsights = (funnel, cart, engagement) => {
    const newInsights = [];

    // Funnel insights
    const funnelSteps = Object.keys(funnel);
    for (let i = 1; i < funnelSteps.length; i++) {
      const currentStep = funnel[funnelSteps[i]];
      const dropoffRate = 100 - parseFloat(currentStep.conversion_rate);
      
      if (dropoffRate > 50) {
        newInsights.push({
          type: 'warning',
          title: `נשירה גבוהה בשלב ${funnelTypes[selectedFunnel].stepNames[i]}`,
          message: `${dropoffRate.toFixed(1)}% מהמשתמשים נושרים בשלב זה`,
          recommendation: `בדוק את חוויית המשתמש בשלב ${funnelTypes[selectedFunnel].stepNames[i]} ושפר את התהליך`
        });
      }
    }

    // Cart abandonment insights
    const abandonmentRate = parseFloat(cart.abandonment_rate);
    if (abandonmentRate > 70) {
      newInsights.push({
        type: 'critical',
        title: 'שיעור נטישת עגלות גבוה',
        message: `${abandonmentRate}% מהעגלות נוטשות`,
        recommendation: 'שקול הוספת תזכורות אימייל, הפחתת עלויות משלוח, או שיפור תהליך התשלום'
      });
    }

    // Engagement insights
    const avgTimeOnPage = parseFloat(engagement.avg_time_on_page || 0);
    if (avgTimeOnPage < 30) {
      newInsights.push({
        type: 'info',
        title: 'זמן שהייה נמוך בדפים',
        message: `זמן שהייה ממוצע: ${avgTimeOnPage.toFixed(1)} שניות`,
        recommendation: 'שפר את תוכן הדפים והפוך אותם למעניינים יותר'
      });
    }

    setInsights(newInsights);
  };

  // Prepare funnel chart data
  const prepareFunnelChartData = () => {
    const steps = funnelTypes[selectedFunnel].steps;
    const stepNames = funnelTypes[selectedFunnel].stepNames;
    
    return steps.map((step, index) => {
      const stepData = funnelData[step];
      return {
        name: stepNames[index],
        users: stepData ? stepData.unique_sessions : 0,
        conversion_rate: stepData ? parseFloat(stepData.conversion_rate) : 0,
        step_number: index + 1
      };
    });
  };

  // Format numbers
  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num?.toString() || '0';
  };

  // Get step icon
  const getStepIcon = (stepName) => {
    const iconMap = {
      'צפייה בדף': EyeIcon,
      'צפייה בקטגוריה': ChartBarIcon,
      'רשימת מוצרים': ChartBarIcon,
      'צפייה במוצר': EyeIcon,
      'חיפוש': MagnifyingGlassIcon,
      'הוספה לעגלה': ShoppingCartIcon,
      'צפייה בעגלה': ShoppingCartIcon,
      'תחילת קנייה': CreditCardIcon,
      'פרטי תשלום': CreditCardIcon,
      'רכישה': CheckCircleIcon
    };
    
    const IconComponent = iconMap[stepName] || UsersIcon;
    return IconComponent;
  };

  // Get conversion rate color
  const getConversionColor = (rate) => {
    if (rate >= 80) return 'text-green-600';
    if (rate >= 60) return 'text-yellow-600';
    if (rate >= 40) return 'text-orange-600';
    return 'text-red-600';
  };

  // Load data on component mount and when filters change
  useEffect(() => {
    fetchAnalyticsData();
  }, [fetchAnalyticsData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        <span className="mr-3 text-gray-600">טוען נתוני משפכי המרה...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-10">
        <div className="text-red-600 mb-4">{error}</div>
        <button
          onClick={fetchAnalyticsData}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          נסה שוב
        </button>
      </div>
    );
  }

  const funnelChartData = prepareFunnelChartData();

  return (
    <div className="container mx-auto p-6 bg-white shadow-lg rounded-lg" dir="rtl">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-800 mb-2 flex items-center">
          <FunnelIcon className="h-8 w-8 ml-2 text-blue-600" />
          משפכי המרה וניתוח מסע המשתמש
        </h1>
        <p className="text-gray-600">
          ניתוח מפורט של תהליך הקנייה ונקודות הנשירה
        </p>
      </div>

      {/* Filters */}
      <div className="mb-6 bg-gray-50 p-4 rounded-lg border">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <CalendarIcon className="h-4 w-4 inline ml-1" />
              טווח תאריכים
            </label>
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              <option value="1d">יום אחרון</option>
              <option value="7d">7 ימים אחרונים</option>
              <option value="30d">30 ימים אחרונים</option>
              <option value="90d">90 ימים אחרונים</option>
              <option value="custom">טווח מותאם</option>
            </select>
          </div>

          {dateRange === 'custom' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">תאריך התחלה</label>
                <input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">תאריך סיום</label>
                <input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                />
              </div>
            </>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <AdjustmentsHorizontalIcon className="h-4 w-4 inline ml-1" />
              סוג משפך
            </label>
            <select
              value={selectedFunnel}
              onChange={(e) => setSelectedFunnel(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              {Object.entries(funnelTypes).map(([key, funnel]) => (
                <option key={key} value={key}>{funnel.name}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="mt-4 flex justify-end">
          <button
            onClick={fetchAnalyticsData}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
          >
            עדכן נתונים
          </button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-6 rounded-lg shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm">סך המשתמשים</p>
              <p className="text-3xl font-bold">
                {formatNumber(funnelChartData[0]?.users || 0)}
              </p>
            </div>
            <UsersIcon className="h-12 w-12 text-blue-200" />
          </div>
        </div>

        <div className="bg-gradient-to-r from-green-500 to-green-600 text-white p-6 rounded-lg shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm">שיעור המרה כולל</p>
              <p className="text-3xl font-bold">
                {funnelChartData[funnelChartData.length - 1]?.conversion_rate?.toFixed(1) || 0}%
              </p>
            </div>
            <CheckCircleIcon className="h-12 w-12 text-green-200" />
          </div>
        </div>

        <div className="bg-gradient-to-r from-red-500 to-red-600 text-white p-6 rounded-lg shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-red-100 text-sm">נטישת עגלות</p>
              <p className="text-3xl font-bold">
                {parseFloat(cartAbandonmentData.abandonment_rate || 0).toFixed(1)}%
              </p>
            </div>
            <ExclamationTriangleIcon className="h-12 w-12 text-red-200" />
          </div>
        </div>

        <div className="bg-gradient-to-r from-purple-500 to-purple-600 text-white p-6 rounded-lg shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-sm">זמן ממוצע בדף</p>
              <p className="text-3xl font-bold">
                {Math.round(engagementData.avg_time_on_page || 0)}s
              </p>
            </div>
            <EyeIcon className="h-12 w-12 text-purple-200" />
          </div>
        </div>
      </div>

      {/* Funnel Visualization */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Funnel Steps */}
        <div className="bg-white p-6 border border-gray-200 rounded-lg shadow-sm">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">שלבי המשפך</h3>
          <div className="space-y-4">
            {funnelChartData.map((step, index) => {
              const IconComponent = getStepIcon(step.name);
              const isLastStep = index === funnelChartData.length - 1;
              const dropoffRate = index > 0 ? 
                ((funnelChartData[index - 1].users - step.users) / funnelChartData[index - 1].users * 100) : 0;

              return (
                <div key={index} className="relative">
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border">
                    <div className="flex items-center">
                      <div className="flex items-center justify-center w-10 h-10 bg-blue-100 rounded-full ml-3">
                        <IconComponent className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-800">{step.name}</h4>
                        <p className="text-sm text-gray-500">שלב {step.step_number}</p>
                      </div>
                    </div>
                    
                    <div className="text-left">
                      <p className="text-lg font-bold text-gray-800">
                        {formatNumber(step.users)}
                      </p>
                      <p className={`text-sm font-medium ${getConversionColor(step.conversion_rate)}`}>
                        {step.conversion_rate.toFixed(1)}%
                      </p>
                    </div>
                  </div>

                  {!isLastStep && (
                    <div className="flex items-center justify-center my-2">
                      <div className="flex items-center">
                        <ArrowDownIcon className="h-4 w-4 text-gray-400 ml-2" />
                        {dropoffRate > 0 && (
                          <span className="text-xs text-red-600 bg-red-50 px-2 py-1 rounded">
                            -{dropoffRate.toFixed(1)}%
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Funnel Chart */}
        <div className="bg-white p-6 border border-gray-200 rounded-lg shadow-sm">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">גרף המשפך</h3>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={funnelChartData} layout="horizontal">
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" />
              <YAxis dataKey="name" type="category" width={100} />
              <Tooltip 
                formatter={(value, name) => [
                  name === 'users' ? formatNumber(value) : `${value}%`,
                  name === 'users' ? 'משתמשים' : 'שיעור המרה'
                ]}
              />
              <Bar dataKey="users" fill="#3B82F6" name="users" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Cart Abandonment Analysis */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="bg-white p-6 border border-gray-200 rounded-lg shadow-sm">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
            <ShoppingCartIcon className="h-5 w-5 ml-2 text-red-500" />
            ניתוח נטישת עגלות
          </h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center p-3 bg-gray-50 rounded">
              <span className="text-gray-700">סך עגלות שנוצרו</span>
              <span className="font-bold text-gray-800">
                {formatNumber(cartAbandonmentData.total_cart_sessions || 0)}
              </span>
            </div>
            <div className="flex justify-between items-center p-3 bg-green-50 rounded">
              <span className="text-gray-700">רכישות שהושלמו</span>
              <span className="font-bold text-green-600">
                {formatNumber(cartAbandonmentData.completed_purchases || 0)}
              </span>
            </div>
            <div className="flex justify-between items-center p-3 bg-red-50 rounded">
              <span className="text-gray-700">עגלות שנוטשו</span>
              <span className="font-bold text-red-600">
                {formatNumber(cartAbandonmentData.abandoned_carts || 0)}
              </span>
            </div>
            <div className="flex justify-between items-center p-3 bg-blue-50 rounded">
              <span className="text-gray-700">שיעור השלמה</span>
              <span className="font-bold text-blue-600">
                {parseFloat(cartAbandonmentData.completion_rate || 0).toFixed(1)}%
              </span>
            </div>
          </div>
        </div>

        {/* Top Exit Pages */}
        <div className="bg-white p-6 border border-gray-200 rounded-lg shadow-sm">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">דפי יציאה מובילים</h3>
          <div className="space-y-3">
            {exitPagesData.slice(0, 5).map((page, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-800 truncate">
                    {page.page_title || 'ללא כותרת'}
                  </p>
                  <p className="text-xs text-gray-500 truncate">{page.page_url}</p>
                </div>
                <div className="text-left">
                  <p className="text-sm font-bold text-red-600">{page.exit_count}</p>
                  <p className="text-xs text-gray-500">יציאות</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Insights and Recommendations */}
      {insights.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-yellow-800 mb-4 flex items-center">
            <ExclamationTriangleIcon className="h-5 w-5 ml-2 text-yellow-600" />
            תובנות והמלצות
          </h3>
          <div className="space-y-3">
            {insights.map((insight, index) => (
              <div key={index} className={`p-4 rounded-md ${
                insight.type === 'critical' ? 'bg-red-100 text-red-800' :
                insight.type === 'warning' ? 'bg-yellow-100 text-yellow-800' :
                insight.type === 'success' ? 'bg-green-100 text-green-800' :
                'bg-blue-100 text-blue-800'
              }`}>
                <h4 className="font-medium mb-1">{insight.title}</h4>
                <p className="text-sm mb-2">{insight.message}</p>
                <p className="text-xs font-medium">💡 {insight.recommendation}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ConversionFunnels;
